import React, { useCallback, useEffect, useState } from 'react';
import { Avatar, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { blockService, BlockedUser } from '../services/blockService';
import { muteService } from '../services/muteService';
import { buddyService } from '../services/buddyService';
import { useNotification } from './NotificationContext';

interface MutedUser { userId: string; displayName: string; profilePhotoUrl?: string | null }

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

const Row: React.FC<{ name: string; photo?: string | null; actionLabel: string; busy: boolean; onAction: () => void }> = ({ name, photo, actionLabel, busy, onAction }) => (
  <Stack direction="row" spacing={1.5} alignItems="center">
    <Avatar src={photo ?? undefined} sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.main' }}>
      {!photo && (initials(name) || '?')}
    </Avatar>
    <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>{name}</Typography>
    <Button size="small" variant="outlined" disabled={busy} onClick={onAction}>{actionLabel}</Button>
  </Stack>
);

/** Settings → Privacy: everyone the diver has blocked or muted, with a way to undo either. */
const BlockedAccountsPanel: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const [blocked, setBlocked] = useState<BlockedUser[]>([]);
  const [muted, setMuted] = useState<MutedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, mutes] = await Promise.all([blockService.getBlocked(), muteService.getMutes()]);
      setBlocked(b);
      const profiles = await Promise.allSettled(mutes.map(m => buddyService.getUserProfile(m.mutedUserId)));
      setMuted(mutes.map((m, i) => {
        const p = profiles[i];
        return p.status === 'fulfilled'
          ? { userId: m.mutedUserId, displayName: p.value.displayName, profilePhotoUrl: p.value.profilePhotoUrl }
          : { userId: m.mutedUserId, displayName: 'A diver' };
      }));
    } catch {
      showError('Failed to load blocked and muted accounts');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  const undo = async (userId: string, action: () => Promise<void>, done: string) => {
    setBusyId(userId);
    try {
      await action();
      showSuccess(done);
      await load();
    } catch {
      showError('That did not work — please try again');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Blocked ({blocked.length})</Typography>
        {blocked.length === 0 && <Typography variant="body2" color="text.secondary">You haven't blocked anyone.</Typography>}
        <Stack spacing={1}>
          {blocked.map(b => (
            <Row key={b.userId} name={b.displayName} photo={b.profilePhotoUrl} actionLabel="Unblock" busy={busyId === b.userId}
              onAction={() => undo(b.userId, () => blockService.unblock(b.userId), `Unblocked ${b.displayName}`)} />
          ))}
        </Stack>
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Muted ({muted.length})</Typography>
        {muted.length === 0 && <Typography variant="body2" color="text.secondary">You haven't muted anyone.</Typography>}
        <Stack spacing={1}>
          {muted.map(m => (
            <Row key={m.userId} name={m.displayName} photo={m.profilePhotoUrl} actionLabel="Unmute" busy={busyId === m.userId}
              onAction={() => undo(m.userId, () => muteService.unmute(m.userId), `Unmuted ${m.displayName}`)} />
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};

export default BlockedAccountsPanel;
